import type { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import type { Paginated } from "_shared/_api/pagination/responses/paginated-response";
import PaginatedResponse from "_shared/_api/pagination/responses/paginated-response";
import { getApiErrorStatus, getErrorMessageFromUnknown } from "_shared/_errors/api-error-message";
import { api, getApiEndpointName } from "_shared/_stores/api";
import type { RootState } from "_shared/_stores/root-reducer";
import type { AppDispatch } from "_shared/_stores/store-types";
import { logSchemaFailure } from "_shared/_utilities/log-schema-failure";
import type EntityMemberQuery from "_shared/entity-members/_api/queries/entity-member-query";
import EntityMemberSummarySchema, {
  EntityMemberSummaryJSON,
} from "_shared/entity-members/entity-member-summary-schema";
import Guid from "_shared/guids/guid";
import GuidSchema from "_shared/guids/guid-schema";
import CreateUserEntityMemberInvitationAPIRequest from "legal-entities/invitations/member-invitations/email-based/_api/commands/create-user-entity-member-invitation-api-command";
import UserEntityMemberInvitation from "legal-entities/invitations/member-invitations/email-based/user-entity-member-invitation";
import { LegalEntityNotFoundError } from "legal-entities/legal-entity";
import { marketplaceCannotEnumeratePeople } from "marketplace/individuals/_api/individual-api";
import { HttpStatusCode } from "axios";
import z from "zod";

export type EntityMemberPageArgs = {
  legalEntityId: string;
  query: EntityMemberQuery;
  /**
   * Set only by a scrolling list: whether this page starts its list again or adds to it. A read
   * without it is cached by RTK Query alone and never touches the pages a scrolling list holds.
   */
  scroll?: EntityMemberScroll;
};

export type EntityMemberScroll = "start" | "next";

const EMPTY_PAGE: Paginated<EntityMemberSummaryJSON> = { data: [], headers: PaginatedResponse.empty().headers };

type UserEntityMemberInvitationArgs = {
  entityId: Guid;
  invitation: UserEntityMemberInvitation;
};

export const entityMemberApi = api.injectEndpoints({
  endpoints: (build) => ({
    createEntityMemberInvitation: build.mutation<Guid, UserEntityMemberInvitationArgs>({
      query: ({ entityId, invitation }) => ({
        url: `legal-entities/${entityId.value}/user-entity-member-invitations`,
        method: "POST",
        body: JSON.stringify(new CreateUserEntityMemberInvitationAPIRequest(invitation)),
        headers: { "Content-Type": "application/json-patch+json" },
      }),
      rawResponseSchema: GuidSchema.GuidJSONZ,
      onSchemaFailure: logSchemaFailure,
      transformResponse: (response: string) => new Guid(GuidSchema.deserialize(response)),
      transformErrorResponse: (response: FetchBaseQueryError, meta) =>
        toEntityMemberApiError(response, meta),
    }),

    // One page of a legal entity's members, searched and ordered on the server (#5442). Each row
    // carries the name, avatar and signing authority, so no second request is needed to draw it.
    getEntityMemberPage: build.query<Paginated<EntityMemberSummaryJSON>, EntityMemberPageArgs>({
      async queryFn({ legalEntityId, query }, _api, _extraOptions, fetchWithBQ) {
        // A restricted onboarding session may not start from an entity and be handed everyone in it
        // (#4559, #4830). The rule is the marketplace's, kept in one place.
        if (marketplaceCannotEnumeratePeople()) return { data: EMPTY_PAGE };
        const result = await fetchWithBQ({
          url: `legal-entities/${legalEntityId}/members`,
          params: query.searchParams,
        });
        if (result.error) {
          return { error: toEntityMemberApiError(result.error, result.meta, legalEntityId) as unknown as FetchBaseQueryError };
        }
        const parsed = z.array(EntityMemberSummarySchema.EntityMemberSummaryJSONZ).safeParse(result.data);
        if (!parsed.success) {
          return { error: new EntityMemberAPIError("getEntityMemberPage", parsed.error) as unknown as FetchBaseQueryError };
        }
        const header = result.meta?.response?.headers.get("x-pagination") ?? "";
        return { data: { data: parsed.data, headers: PaginatedResponse.parsePaginationHeader(header) } };
      },
      providesTags: (_result, _error, { legalEntityId }) => [{ type: "EntityMember" as const, id: legalEntityId }],
    }),


  }),
});

/**
 * Redraws a member's signing authority on every roster page already cached, from the answer of a
 * grant or a removal the caller has just made.
 *
 * The roster reads signing authority from Permissions, and a permission write invalidates none of
 * the roster's tags, so without this the Role cell would go on naming the old role until the next
 * page, sort or search. Every cached page is visited because the page is keyed by its query, which
 * the permission write never sees.
 */
export function markMemberSigningAuthority(userId: Guid, hasSigningAuthority: boolean) {
  return (dispatch: AppDispatch, getState: () => RootState) => {
    for (const args of entityMemberApi.util.selectCachedArgsForQuery(getState(), "getEntityMemberPage")) {
      dispatch(
        entityMemberApi.util.updateQueryData("getEntityMemberPage", args, (page) => {
          for (const member of page.data) {
            if (member.userId === userId.value) member.hasSigningAuthority = hasSigningAuthority;
          }
        }),
      );
    }
  };
}

export const {
  useCreateEntityMemberInvitationMutation,
  useGetEntityMemberPageQuery,
  useLazyGetEntityMemberPageQuery,
} = entityMemberApi;

export class InvalidInvitationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidInvitationError";
  }
}

export class EntityMemberAPIError extends Error {
  constructor(methodName: string | undefined, public readonly error: unknown) {
    const endpointName = methodName ? `.${methodName}` : "";
    super(`Error in entityMemberApi${endpointName}: ${getErrorMessageFromUnknown(error) ?? "An error occurred"}`);
  }
}

export class UnauthorizedEntityMemberAccessError extends Error {
  constructor(entityId: Guid) {
    super(`Unauthorized access to legal entity members for entity ${entityId}`);
  }
}

function toEntityMemberApiError(response: FetchBaseQueryError, meta?: unknown, legalEntityId?: string): Error {
  const status = getApiErrorStatus(response);
  const entityId = legalEntityId ? Guid.fromJSON(legalEntityId) : undefined;

  if (status === HttpStatusCode.BadRequest) {
    return new InvalidInvitationError(getErrorMessageFromUnknown(response) ?? "Request failed with status code 400");
  }
  if (status === HttpStatusCode.Forbidden && entityId) {
    return new UnauthorizedEntityMemberAccessError(entityId);
  }
  if (status === HttpStatusCode.NotFound && entityId) {
    return new LegalEntityNotFoundError(entityId);
  }

  return new EntityMemberAPIError(getApiEndpointName(meta), response);
}
